import { useEffect, useRef, useState } from 'react'
import { X, Keyboard, Mic, MicOff, PhoneOff, Send } from 'lucide-react'
import { useBen } from './useBen'

const STATUS_LABEL = {
  disconnected: 'Prêt à vous écouter',
  connecting: 'Connexion…',
  connected: 'En ligne',
  disconnecting: 'Fin de session…',
  error: 'Erreur',
}

const USER_ACTIVITY_THROTTLE_MS = 750

export default function BenModal({ onClose }) {
  const [transcript, setTranscript] = useState([])
  const [textInput, setTextInput] = useState('')
  const [viewportMetrics, setViewportMetrics] = useState({
    height: null,
    offsetTop: 0,
  })
  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  const autoStartedRef = useRef(false)
  const hadConnectedRef = useRef(false)
  const lastUserActivityAtRef = useRef(0)

  const ben = useBen({
    onTranscript: (m) => {
      setTranscript((prev) => [
        ...prev,
        { id: m.event_id ?? Date.now(), source: m.source, text: m.message },
      ])
    },
  })

  // Auto-start on open (click that opened the modal counts as the user gesture).
  useEffect(() => {
    if (autoStartedRef.current) return
    if (ben.status !== 'disconnected') return
    autoStartedRef.current = true
    ben.start()
  }, [ben])

  // Auto-close after server-side end_call so the user doesn't have to dismiss
  // the modal manually. Only fires after we've reached `connected` at least
  // once — guards against closing on a failed connect.
  useEffect(() => {
    if (ben.status === 'connected') {
      hadConnectedRef.current = true
      return
    }
    if (ben.status === 'disconnected' && hadConnectedRef.current) {
      const t = setTimeout(onClose, 1000)
      return () => clearTimeout(t)
    }
  }, [ben.status, onClose])

  // Auto-scroll transcript
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [transcript])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const visualViewport = window.visualViewport
    const updateViewport = () => {
      setViewportMetrics({
        height: Math.round(visualViewport?.height || window.innerHeight || 0),
        offsetTop: Math.round(visualViewport?.offsetTop || 0),
      })
    }

    updateViewport()
    visualViewport?.addEventListener('resize', updateViewport)
    visualViewport?.addEventListener('scroll', updateViewport)
    window.addEventListener('resize', updateViewport)

    return () => {
      visualViewport?.removeEventListener('resize', updateViewport)
      visualViewport?.removeEventListener('scroll', updateViewport)
      window.removeEventListener('resize', updateViewport)
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return

    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [])

  const handleClose = () => {
    ben.stop()
    onClose()
  }

  const statusLabel = STATUS_LABEL[ben.status] ?? ben.status
  const isConnected = ben.status === 'connected'

  const signalWrittenActivity = () => {
    if (!isConnected) return
    if (!ben.isMuted) ben.setMuted(true)

    const now = Date.now()
    if (now - lastUserActivityAtRef.current < USER_ACTIVITY_THROTTLE_MS) return
    lastUserActivityAtRef.current = now
    ben.noteUserActivity()
  }

  const enterWrittenMode = () => {
    signalWrittenActivity()
    inputRef.current?.focus()
  }

  const handleTextChange = (e) => {
    setTextInput(e.target.value)
    signalWrittenActivity()
  }

  const handleMuteToggle = () => {
    ben.toggleMute()
    if (!ben.isMuted) {
      inputRef.current?.focus()
    }
  }

  const handleSendText = (e) => {
    e.preventDefault()
    const trimmed = textInput.trim()
    if (!trimmed || !isConnected) return
    ben.setMuted(true)
    ben.noteUserActivity()
    ben.sendText(trimmed)
    setTranscript((prev) => [
      ...prev,
      { id: Date.now(), source: 'user', text: trimmed },
    ])
    setTextInput('')
  }

  const visualViewportHeight = viewportMetrics.height
    ? `${viewportMetrics.height}px`
    : '100dvh'
  const visualViewportTop = viewportMetrics.offsetTop
    ? `${viewportMetrics.offsetTop}px`
    : '0px'

  return (
    <div
      className="ben-modal-shell"
      role="dialog"
      aria-modal="true"
      aria-label="Conversation avec Ben"
      style={{
        position: 'fixed',
        top: 'var(--ben-vv-top)',
        right: 0,
        bottom: 'auto',
        left: 0,
        height: 'var(--ben-vvh)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        padding: '16px',
        pointerEvents: 'none',
        '--ben-vvh': visualViewportHeight,
        '--ben-vv-top': visualViewportTop,
      }}
    >
      {/* Click-outside overlay */}
      <div
        onClick={handleClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(10, 10, 26, 0.35)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          pointerEvents: 'auto',
          animation: 'benFadeIn 0.2s ease',
        }}
      />

      <div
        className="ben-modal"
        style={{
          position: 'relative',
          pointerEvents: 'auto',
          width: '100%',
          maxWidth: '400px',
          height: '600px',
          maxHeight: 'calc(100vh - 32px)',
          borderRadius: '16px',
          overflow: 'hidden',
          background: '#fff',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'benSlideIn 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {/* Header */}
        <div
          className="ben-modal-header"
          style={{
            background: 'linear-gradient(135deg, #2F2F84 0%, #6466B0 100%)',
            color: '#fff',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px solid rgba(255,255,255,0.4)',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            <img
              src="/team/ben.jpg"
              alt="Ben"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
            {ben.isSpeaking && (
              <div
                style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '50%',
                  border: '2px solid #8B6F47',
                  animation: 'benPulse 1.2s ease-in-out infinite',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Ben</div>
            <div
              style={{
                fontSize: '0.78rem',
                opacity: 0.85,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: isConnected ? '#4ade80' : '#fbbf24',
                  display: 'inline-block',
                  boxShadow: isConnected
                    ? '0 0 8px rgba(74, 222, 128, 0.6)'
                    : 'none',
                }}
              />
              {statusLabel}
            </div>
          </div>
          <button
            onClick={handleClose}
            aria-label="Fermer"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.28)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.15)'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Transcript */}
        <div
          className="ben-modal-transcript"
          ref={scrollRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '16px',
            background: '#f8f9fc',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {transcript.length === 0 && ben.status === 'connected' && (
            <div
              style={{
                color: '#6466B0',
                fontSize: '0.85rem',
                textAlign: 'center',
                padding: '24px 12px',
              }}
            >
              Posez votre question à voix haute ou par écrit.
            </div>
          )}
          {ben.status === 'connecting' && (
            <div
              style={{
                color: '#6466B0',
                fontSize: '0.85rem',
                textAlign: 'center',
                padding: '24px 12px',
              }}
            >
              Connexion en cours…
            </div>
          )}
          {transcript.map((m) => (
            <div
              key={m.id}
              style={{
                alignSelf: m.source === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '80%',
                padding: '10px 14px',
                borderRadius: '14px',
                background:
                  m.source === 'user'
                    ? 'linear-gradient(135deg, #6466B0 0%, #8186C8 100%)'
                    : '#2F2F84',
                color: '#fff',
                fontSize: '0.9rem',
                lineHeight: 1.45,
                wordWrap: 'break-word',
              }}
            >
              {m.text}
            </div>
          ))}
        </div>

        {/* Controls */}
        <div
          className="ben-modal-controls"
          style={{
            borderTop: '1px solid #e5e7eb',
            background: '#fff',
            padding: '12px',
            flexShrink: 0,
          }}
        >
          <form
            onSubmit={handleSendText}
            style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}
          >
            <input
              className="ben-modal-input"
              ref={inputRef}
              type="text"
              inputMode="text"
              enterKeyHint="send"
              autoComplete="off"
              value={textInput}
              onChange={handleTextChange}
              onPointerDown={signalWrittenActivity}
              placeholder="Écrivez à Ben…"
              disabled={!isConnected}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: '24px',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'border-color 0.2s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#6466B0'
                signalWrittenActivity()
              }}
              onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
            />
            <button
              type="submit"
              disabled={!isConnected || !textInput.trim()}
              aria-label="Envoyer"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                background:
                  isConnected && textInput.trim() ? '#2F2F84' : '#d1d5db',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor:
                  isConnected && textInput.trim() ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s ease',
              }}
            >
              <Send size={16} />
            </button>
          </form>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              minHeight: '20px',
              marginBottom: '10px',
              color: ben.isMuted ? '#b91c1c' : '#4b5563',
              fontSize: '0.76rem',
              fontWeight: 600,
            }}
          >
            {ben.isMuted ? <Keyboard size={13} /> : <Mic size={13} />}
            <span>{ben.isMuted ? 'Mode discret - micro coupé' : 'Micro ouvert'}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <button
              onClick={enterWrittenMode}
              disabled={!isConnected}
              aria-label="Passer en mode discret"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: ben.isMuted ? '#2F2F84' : '#f3f4f6',
                color: ben.isMuted ? '#fff' : '#2F2F84',
                cursor: isConnected ? 'pointer' : 'not-allowed',
                opacity: isConnected ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <Keyboard size={18} />
            </button>

            <button
              onClick={handleMuteToggle}
              disabled={!isConnected}
              aria-label={ben.isMuted ? 'Activer le micro' : 'Couper le micro'}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: ben.isMuted ? '#ef4444' : '#f3f4f6',
                color: ben.isMuted ? '#fff' : '#2F2F84',
                cursor: isConnected ? 'pointer' : 'not-allowed',
                opacity: isConnected ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              {ben.isMuted ? <MicOff size={18} /> : <Mic size={18} />}
            </button>

            <button
              onClick={handleClose}
              aria-label="Terminer"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                border: 'none',
                background: '#ef4444',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#dc2626')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#ef4444')}
            >
              <PhoneOff size={18} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes benFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes benSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes benPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(139, 111, 71, 0.6); }
          50% { box-shadow: 0 0 0 8px rgba(139, 111, 71, 0); }
        }
        @keyframes benMobileSlideIn {
          from { opacity: 0.9; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 480px) {
          .ben-bubble-label { display: none; }
          .ben-modal-shell {
            align-items: stretch !important;
            justify-content: stretch !important;
            padding: 0 !important;
          }
          .ben-modal {
            width: 100% !important;
            max-width: 100% !important;
            height: 100vh !important;
            height: var(--ben-vvh, 100dvh) !important;
            max-height: 100vh !important;
            max-height: var(--ben-vvh, 100dvh) !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            animation: benMobileSlideIn 0.22s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
          }
          .ben-modal-header {
            padding: calc(12px + env(safe-area-inset-top, 0px)) 16px 12px !important;
          }
          .ben-modal-transcript {
            padding: 12px !important;
            -webkit-overflow-scrolling: touch;
          }
          .ben-modal-controls {
            padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px)) !important;
          }
          .ben-modal-input {
            font-size: 16px !important;
            line-height: 1.25 !important;
          }
        }
      `}</style>
    </div>
  )
}
